import {Input, Modal} from 'antd'
import {
    exam_createExaminationInfoAnswer_Api,
    exam_deleteExaminationInfoAnswer_Api,
    exam_editExaminationInfoAnswer_Api,
} from 'apis'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {Formik} from 'formik'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {showDeleteConfirmDialog} from '../factory/createConfirmDialog'
import createContextMenu from '../factory/createContextMenu'
import {notifyOnError} from '../factory/createErrorEvent'
import {createControlledFormField} from '../factory/createFormField'
import {NotificationActionTypes, successNotify,} from '../factory/createNotification'
import {bindQueryParam} from '../functions/routerHelper'
import {BiSolidEdit, BiTrash} from "react-icons/bi";

const ExaminationInfoAnswersContext = React.createContext({})

const formSchema = {
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
        }),
      },
    ],
  },
  desscription: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 3,
          placeholder: 'description',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  order_sort: {
    title: 'order',
    children: [
      {
        name: 'order_sort',
        component: createControlledFormField({
          min: 0,
          type: 'number',
          placeholder: 'order',
        }),
      },
    ],
  },
}

const CreateInfoAnswerModal = ({ initialValues, onSubmit = NullAsDefault }) => {
  const t = useTranslate()

  const _initialValues = useMemo(() => {
    return {
      order_sort: 0,
      ...(initialValues || {}),
    }
  }, [initialValues])

  const validate = (values) => {
    const { title } = values || {}

    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }
    return errors
  }

  return (
    <Formik
      validate={validate}
      initialValues={_initialValues}
      onSubmit={(values) => {
        const changed = shallowDiff(values, _initialValues)
        onSubmit(
          _.omitBy(
            {
              answer_type: values?.answer_type || undefined,
              ...changed,
            },
            _.isUndefined
          )
        )
      }}>
      <React.Fragment>
        <FieldsFactory
          formSchema={[
            formSchema.title,
            formSchema.desscription,
            formSchema.order_sort,
          ]}
        />
        <FormActionBar submitText="create" />
      </React.Fragment>
    </Formik>
  )
}

const EditInfoAnswerModal = ({ params, initialValues, onSubmit = NullAsDefault }) => {
  const t = useTranslate()

  const { examination_info_id } = params || {}

  const validate = (values) => {
    const { title } = values || {}

    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }
    return errors
  }

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      onSubmit={(values) => {
        const changed = shallowDiff(values, initialValues)
        onSubmit(
          _.omitBy(
            {
              answer_type: values?.answer_type || undefined,
              ...changed,
              // group_examination_info_id: examination_info_id,
              // group_examination_info_answer_id:
              //   'edb43d60-f30e-11ed-8a9c-c5926d26b5ab',
            },
            _.isUndefined
          )
        )
      }}>
      <React.Fragment>
        <FieldsFactory
          formSchema={[
            formSchema.title,
            formSchema.desscription,
            formSchema.order_sort,
          ]}
        />
        <FormActionBar submitText="save" />
      </React.Fragment>
    </Formik>
  )
}

export const ExaminationInfoAnswersProvider = ({
  examination_info_id,
  children,
  ...props
}) => {
  const t = useTranslate()

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const [refreshKey, setRefreshKey] = useState()

  const onError = notifyOnError(t)

  const onCancel = () => {
    setAction({})
  }

  const reload = () => {
    setRefreshKey(Date.now())
    onCancel()
  }

  const { handleAsyncAction: onCreate } = useAsyncAction({
    apiInfo: exam_createExaminationInfoAnswer_Api,
    query: bindQueryParam({ id: examination_info_id }),
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      reload()
    },
  })

  const { handleAsyncAction: onUpdate } = useAsyncAction({
    apiInfo: exam_editExaminationInfoAnswer_Api,
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      reload()
    },
  })

  const { handleAsyncAction: onDelete } = useAsyncAction({
    apiInfo: exam_deleteExaminationInfoAnswer_Api,
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      reload()
    },
  })

  const confirmDelete = useCallback(
    (id) => {
      if (id) {
        showDeleteConfirmDialog({
          translate: t,
          onOk: () => {
            onDelete({}, { id })
          },
        })
      }
    },
    [onDelete]
  )

  const handleAction = (action, params) => {
    const { id } = params || {}
    switch (action) {
      case CRUD.CREATE:
      case CRUD.UPDATE:
        setAction({ type: action, value: params })
        break
      case CRUD.DELETE:
        confirmDelete(id)
        break
      default:
        break
    }
  }

  const renderContextMenu = useCallback(
    (item) =>
      createContextMenu(
        [
          {
            icon: <BiSolidEdit size={14} className="text-blue-500"/>,
            label: 'edit',
            key: CRUD.UPDATE,
          },
          {
            icon: <BiTrash size={14} className="text-primary-300"/>,
            label: 'delete',
            key: CRUD.DELETE,
          },
        ].filter((e) => item),
        (key) => {
          handleAction(key, item)
        }
      ),
    []
  )

  const modal = useMemo(() => {
    const { id } = action.value || {}
    let title
    let Content = NullAsDefault
    let params = { title: undefined }
    switch (action.type) {
      case CRUD.CREATE:
        title = (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('create')}
          </div>
        )
        params = {
          onCancel,
          onSubmit: (values) => {
            onCreate(values)
          },
        }
        Content = CreateInfoAnswerModal
        break
      case CRUD.UPDATE:
        title = (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('update')}
          </div>
        )
        params = {
          onCancel,
          onSubmit: (values) => {
            onUpdate(values, { id })
          },
        }
        Content = EditInfoAnswerModal
        break
      default:
        break
    }
    return (
      <Modal
        footer={null}
        title={title}
        onCancel={onCancel}
        open={action.type}
        destroyOnHidden={true}>
        <Content
          {...params}
          params={{ examination_info_id }}
          initialValues={action.value || {}}
        />
      </Modal>
    )
  }, [action, examination_info_id])

  return (
    <ExaminationInfoAnswersContext.Provider
      key={examination_info_id}
      value={{
        action,
        refreshKey,
        onCreate,
        onUpdate,
        onDelete,
        handleAction,
        examination_info_id,
        renderContextMenu,
        ...props,
      }}>
      <React.Fragment>
        {children}
        {modal}
      </React.Fragment>
    </ExaminationInfoAnswersContext.Provider>
  )
}

export default ExaminationInfoAnswersContext
